import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, FolderKanban, RefreshCw, Search, Plus, X, Upload, ExternalLink,
  Fingerprint, Download, ShieldCheck, Clock, AlertTriangle,
  ChevronRight, FileText, Layers,
} from "lucide-react";

// Strip transport prefix ("user:foo@bar" → "foo@bar") for readable chain-of-custody display.
function displayActor(raw) {
  if (!raw) return "system";
  const s = String(raw);
  const colon = s.indexOf(":");
  if (colon > 0 && colon < 20) return s.slice(colon + 1);
  return s;
}
import "./AdminUATCycles.css";
import "./AdminEvidence.css";
import { Pill } from "@/components/regulatory/Pill";
import { fmtDate } from "@/components/regulatory/constants";

const STATUS_TONE = {
  draft:     { color: "#334155", bg: "#F1F5F9", label: "Draft" },
  submitted: { color: "#1E40AF", bg: "#DBEAFE", label: "Submitted" },
  approved:  { color: "#065F46", bg: "#D1FAE5", label: "Approved" },
  expired:   { color: "#991B1B", bg: "#FEE2E2", label: "Expired" },
  archived:  { color: "#64748B", bg: "#F8FAFC", label: "Archived" },
  replaced:  { color: "#7C3AED", bg: "#EDE9FE", label: "Replaced" },
};
const STORAGE_LABEL = {
  internal: "Internal",
  external_reference: "External Reference",
  hash_only: "Hash-Only",
};

function KpiCard({ label, value, tone, icon: Icon, testId, hint }) {
  return (
    <div className={`hw-ev__kpi hw-ev__kpi--${tone || "default"}`} data-testid={testId}>
      <div className="hw-ev__kpi-label">
        {Icon ? <Icon className="w-3.5 h-3.5" /> : null}
        {label}
      </div>
      <div className="hw-ev__kpi-value">{value}</div>
      {hint ? <div className="hw-ev__kpi-hint">{hint}</div> : null}
    </div>
  );
}

// ============================================================================
// Create Evidence dialog
// ============================================================================
function CreateEvidenceDialog({ types, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "", evidence_type: "policy", storage_model: "hash_only",
    frameworks: "", control_ids: "", owner: "", department: "",
    effective_date: "", expiration_date: "", tags: "", notes: "",
    // Internal
    file_name: "", mime_type: "", content_base64: "",
    // Hash-only
    sha256: "", size_bytes: "",
    // External reference
    external_url: "", external_repo_type: "sharepoint",
  });
  const [saving, setSaving] = useState(false);

  const onFile = async (file) => {
    if (!file) return;
    // Read as base64
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = (reader.result || "").toString().split(",")[1] || "";
      setForm((f) => ({
        ...f,
        content_base64: b64,
        file_name: file.name,
        mime_type: file.type || "application/octet-stream",
      }));
    };
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        evidence_type: form.evidence_type,
        storage_model: form.storage_model,
        frameworks: form.frameworks.split(",").map(s => s.trim()).filter(Boolean),
        control_ids: form.control_ids.split(",").map(s => s.trim()).filter(Boolean),
        owner: form.owner || null,
        department: form.department || null,
        effective_date: form.effective_date || null,
        expiration_date: form.expiration_date || null,
        tags: form.tags.split(",").map(s => s.trim()).filter(Boolean),
        notes: form.notes || null,
      };
      if (form.storage_model === "internal") {
        payload.content_base64 = form.content_base64;
        payload.file_name = form.file_name;
        payload.mime_type = form.mime_type;
      } else if (form.storage_model === "hash_only") {
        payload.sha256 = form.sha256;
        payload.size_bytes = form.size_bytes ? Number(form.size_bytes) : null;
        payload.file_name = form.file_name || null;
        payload.mime_type = form.mime_type || null;
      } else if (form.storage_model === "external_reference") {
        payload.external_reference = {
          url: form.external_url,
          repository_type: form.external_repo_type,
          sha256: form.sha256 || null,
          file_name: form.file_name || null,
          mime_type: form.mime_type || null,
        };
      }
      await api.post("/admin/evidence", payload);
      toast.success("Evidence created");
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e, "Create failed"));
    } finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="hw-ev__drawer-backdrop" onClick={onClose}>
      <div className="hw-ev__drawer" onClick={(e) => e.stopPropagation()}
           data-testid="evidence-create-drawer">
        <div className="hw-ev__drawer-head">
          <div>
            <div className="hw-ev__drawer-crumb">New Evidence</div>
            <div className="hw-ev__drawer-title"><Plus className="w-4 h-4" /> Add Evidence Item</div>
          </div>
          <button type="button" className="hw-uat-cycles__icon-btn" onClick={onClose}
                  data-testid="evidence-create-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="hw-ev__drawer-body">
          <div className="hw-ev__form">
            <label>Title *
              <input type="text" value={form.title} onChange={set("title")}
                     data-testid="ev-form-title" required />
            </label>
            <div className="hw-ev__form-grid">
              <label>Evidence type
                <select value={form.evidence_type} onChange={set("evidence_type")}
                        data-testid="ev-form-type">
                  {(types?.evidence_types || []).map(t => (
                    <option key={t.key} value={t.key}>{t.label}</option>
                  ))}
                </select>
              </label>
              <label>Storage model
                <select value={form.storage_model} onChange={set("storage_model")}
                        data-testid="ev-form-storage">
                  <option value="hash_only">Hash-Only (metadata + SHA-256)</option>
                  <option value="internal">Internal (upload file)</option>
                  <option value="external_reference">External Reference</option>
                </select>
              </label>
              <label>Owner
                <input type="text" value={form.owner} onChange={set("owner")}
                       placeholder="Owner name" data-testid="ev-form-owner" />
              </label>
              <label>Department
                <input type="text" value={form.department} onChange={set("department")}
                       placeholder="e.g. IT / Compliance" data-testid="ev-form-dept" />
              </label>
              <label>Frameworks (comma-separated)
                <input type="text" value={form.frameworks} onChange={set("frameworks")}
                       placeholder="hipaa, soc2" data-testid="ev-form-frameworks" />
              </label>
              <label>Linked control IDs (comma-separated)
                <input type="text" value={form.control_ids} onChange={set("control_ids")}
                       placeholder="AC-CTRL-001, EN-CTRL-002" data-testid="ev-form-controls" />
              </label>
              <label>Effective date
                <input type="date" value={form.effective_date} onChange={set("effective_date")}
                       data-testid="ev-form-effective" />
              </label>
              <label>Expiration date
                <input type="date" value={form.expiration_date} onChange={set("expiration_date")}
                       data-testid="ev-form-expiration" />
              </label>
            </div>

            {form.storage_model === "internal" && (
              <div className="hw-ev__form-section" data-testid="ev-form-internal-section">
                <h5><Upload className="w-3.5 h-3.5" style={{ display: "inline", marginRight: "0.3rem", verticalAlign: "text-bottom" }} /> Upload file</h5>
                <input type="file" onChange={(e) => onFile(e.target.files?.[0])}
                       data-testid="ev-form-file-input" />
                {form.file_name ? (
                  <div className="hw-ev__hint">
                    <FileText className="w-3.5 h-3.5" /> {form.file_name}
                    {form.mime_type ? ` (${form.mime_type})` : ""}
                  </div>
                ) : null}
              </div>
            )}
            {form.storage_model === "external_reference" && (
              <div className="hw-ev__form-section" data-testid="ev-form-external-section">
                <h5>External reference</h5>
                <div className="hw-ev__form-grid">
                  <label>Repository
                    <select value={form.external_repo_type} onChange={set("external_repo_type")}
                            data-testid="ev-form-external-repo">
                      <option value="sharepoint">SharePoint</option>
                      <option value="onedrive">OneDrive</option>
                      <option value="google_drive">Google Drive</option>
                      <option value="azure_blob">Azure Blob Storage</option>
                      <option value="amazon_s3">Amazon S3</option>
                      <option value="network_share">Network Share</option>
                      <option value="other">Other</option>
                    </select>
                  </label>
                  <label>Secure URL *
                    <input type="url" value={form.external_url} onChange={set("external_url")}
                           placeholder="https://…" data-testid="ev-form-external-url" />
                  </label>
                  <label>File name
                    <input type="text" value={form.file_name} onChange={set("file_name")}
                           data-testid="ev-form-file-name" />
                  </label>
                  <label>SHA-256 (optional, enables integrity check)
                    <input type="text" value={form.sha256} onChange={set("sha256")}
                           placeholder="64-char hex" data-testid="ev-form-sha256-ext" />
                  </label>
                </div>
              </div>
            )}
            {form.storage_model === "hash_only" && (
              <div className="hw-ev__form-section" data-testid="ev-form-hash-section">
                <h5>Hash-only integrity</h5>
                <p className="hw-ev__hint">
                  HarborWell Connect stores only the SHA-256 fingerprint. Your organization retains the file.
                </p>
                <div className="hw-ev__form-grid">
                  <label>SHA-256 * (64-char hex)
                    <input type="text" value={form.sha256} onChange={set("sha256")}
                           required data-testid="ev-form-sha256-hash" />
                  </label>
                  <label>File size (bytes)
                    <input type="number" value={form.size_bytes} onChange={set("size_bytes")}
                           data-testid="ev-form-size" />
                  </label>
                  <label>File name
                    <input type="text" value={form.file_name} onChange={set("file_name")}
                           data-testid="ev-form-hash-filename" />
                  </label>
                  <label>MIME type
                    <input type="text" value={form.mime_type} onChange={set("mime_type")}
                           placeholder="application/pdf" data-testid="ev-form-hash-mime" />
                  </label>
                </div>
              </div>
            )}
            <label>Tags (comma-separated)
              <input type="text" value={form.tags} onChange={set("tags")}
                     placeholder="hipaa, quarterly" data-testid="ev-form-tags" />
            </label>
            <label>Notes
              <textarea rows={2} value={form.notes} onChange={set("notes")}
                        data-testid="ev-form-notes" />
            </label>
          </div>
          <div className="hw-ev__actions">
            <button type="button" className="hw-reg__btn" onClick={onClose}>Cancel</button>
            <button type="button" className="hw-reg__btn hw-reg__btn--primary"
                    onClick={submit} disabled={saving || !form.title}
                    data-testid="ev-form-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Create evidence
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Evidence detail drawer — includes verification, lifecycle, chain of custody
// ============================================================================
function EvidenceDetail({ evidenceId, onClose, onChanged }) {
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifyHash, setVerifyHash] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [newStatus, setNewStatus] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/evidence/${evidenceId}`);
      setRow(data);
      setNewStatus(data.status);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load evidence"));
      onClose();
    } finally { setLoading(false); }
  }, [evidenceId, onClose]);

  useEffect(() => { load(); }, [load]);

  const download = async () => {
    try {
      const token = localStorage.getItem("hw_access_token");
      const resp = await fetch(
        `${api.defaults.baseURL}/admin/evidence/${evidenceId}/download`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const sha = resp.headers.get("X-HWC-Evidence-SHA256") || "";
      const blob = await resp.blob();
      const disp = resp.headers.get("Content-Disposition") || "";
      const nameMatch = disp.match(/filename="([^"]+)"/);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = nameMatch ? nameMatch[1] : evidenceId;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded — SHA-256: ${sha.slice(0, 12)}…`);
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Download failed"));
    }
  };

  const verify = async () => {
    if (!verifyHash) return;
    setVerifying(true);
    try {
      const { data } = await api.post(`/admin/evidence/${evidenceId}/verify`,
        { sha256: verifyHash, source: "manual_ui" });
      if (data.matches) toast.success("✓ SHA-256 matches — integrity verified");
      else toast.error("✗ SHA-256 mismatch");
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Verify failed"));
    } finally { setVerifying(false); }
  };

  const transition = async () => {
    try {
      await api.post(`/admin/evidence/${evidenceId}/transition`,
        { status: newStatus });
      toast.success(`Status → ${newStatus}`);
      load(); onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e, "Transition failed"));
    }
  };

  return (
    <div className="hw-ev__drawer-backdrop" onClick={onClose}>
      <div className="hw-ev__drawer" onClick={(e) => e.stopPropagation()}
           data-testid="evidence-detail-drawer">
        <div className="hw-ev__drawer-head">
          <div>
            <div className="hw-ev__drawer-crumb">Evidence</div>
            <div className="hw-ev__drawer-title">
              <FolderKanban className="w-4 h-4" />
              <code>{evidenceId}</code>
              {row?.title ? <span>{row.title}</span> : null}
            </div>
          </div>
          <button type="button" className="hw-uat-cycles__icon-btn" onClick={onClose}
                  data-testid="evidence-detail-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="hw-ev__drawer-body">
          {loading || !row ? (
            <div className="hw-uat-cycles__loading">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              <section className="hw-ev__section">
                <h4>Overview</h4>
                <dl className="hw-ev__dl">
                  <dt>Title</dt><dd>{row.title}</dd>
                  <dt>Type</dt><dd>{row.evidence_type}</dd>
                  <dt>Storage model</dt><dd><strong>{STORAGE_LABEL[row.storage_model]}</strong></dd>
                  <dt>Status</dt><dd><Pill tone={STATUS_TONE[row.status]}>{STATUS_TONE[row.status]?.label}</Pill></dd>
                  <dt>Owner</dt><dd>{row.owner || "—"}</dd>
                  <dt>Department</dt><dd>{row.department || "—"}</dd>
                  <dt>Frameworks</dt><dd>{(row.frameworks || []).join(", ") || "—"}</dd>
                  <dt>Linked controls</dt><dd>{(row.control_ids || []).join(", ") || "—"}</dd>
                  <dt>Effective date</dt><dd>{fmtDate(row.effective_date)}</dd>
                  <dt>Expiration date</dt><dd>{fmtDate(row.expiration_date)}</dd>
                  <dt>Tags</dt><dd>{(row.tags || []).join(", ") || "—"}</dd>
                  <dt>Version</dt><dd>v{row.version}</dd>
                </dl>
              </section>

              <section className="hw-ev__section">
                <h4>Integrity</h4>
                <dl className="hw-ev__dl">
                  <dt>Algorithm</dt><dd>SHA-256</dd>
                  <dt>SHA-256</dt>
                  <dd><code className="hw-ev__code">{row.integrity?.sha256 || "—"}</code></dd>
                  <dt>Size</dt>
                  <dd>{row.integrity?.size_bytes ? `${row.integrity.size_bytes} bytes` : "—"}</dd>
                  <dt>File name</dt><dd>{row.file_metadata?.file_name || "—"}</dd>
                  <dt>Verifications</dt><dd>{(row.verifications || []).length}</dd>
                </dl>
                <div className="hw-ev__form-grid">
                  <label>Verify against SHA-256
                    <input type="text" value={verifyHash} onChange={(e) => setVerifyHash(e.target.value)}
                           placeholder="Paste computed SHA-256"
                           data-testid="evidence-verify-input" />
                  </label>
                  <div className="hw-ev__verify-btns">
                    <button type="button" className="hw-reg__btn"
                            onClick={verify} disabled={verifying || !verifyHash}
                            data-testid="evidence-verify-btn">
                      {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Fingerprint className="w-4 h-4" />}
                      Verify integrity
                    </button>
                    {row.storage_model === "internal" && (
                      <button type="button" className="hw-reg__btn"
                              onClick={download} data-testid="evidence-download-btn">
                        <Download className="w-4 h-4" /> Download
                      </button>
                    )}
                    {row.storage_model === "external_reference" && row.external_reference?.url && (
                      <a className="hw-reg__btn" href={row.external_reference.url}
                         target="_blank" rel="noreferrer noopener"
                         data-testid="evidence-external-link">
                        <ExternalLink className="w-4 h-4" /> Open external
                      </a>
                    )}
                  </div>
                </div>
              </section>

              <section className="hw-ev__section">
                <h4>Lifecycle</h4>
                <div className="hw-ev__form-grid">
                  <label>Transition status
                    <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)}
                            data-testid="evidence-status-select">
                      <option value="draft">Draft</option>
                      <option value="submitted">Submitted</option>
                      <option value="approved">Approved</option>
                      <option value="expired">Expired</option>
                      <option value="archived">Archived</option>
                      <option value="replaced">Replaced</option>
                    </select>
                  </label>
                  <div className="hw-ev__verify-btns">
                    <button type="button" className="hw-reg__btn hw-reg__btn--primary"
                            onClick={transition} disabled={newStatus === row.status}
                            data-testid="evidence-transition-btn">
                      Apply
                    </button>
                  </div>
                </div>
              </section>

              <section className="hw-ev__section">
                <h4>Version History ({(row.version_history || []).length})</h4>
                <ul className="hw-ev__timeline" data-testid="evidence-version-history">
                  {(row.version_history || []).slice().reverse().map((v, i) => (
                    <li key={i}>
                      <strong>v{v.version}</strong>
                      <span>{fmtDate(v.changed_at)}</span>
                      <span>{v.changed_by}</span>
                      <span>{v.change_note}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <section className="hw-ev__section">
                <h4>Chain of Custody ({(row.chain_of_custody || []).length})</h4>
                <ul className="hw-ev__timeline" data-testid="evidence-chain-of-custody">
                  {(row.chain_of_custody || []).slice().reverse().slice(0, 12).map((c, i) => (
                    <li key={i}>
                      <strong>{c.action}</strong>
                      <span>{fmtDate(c.at)}</span>
                      <span title={c.actor || "system"}>{displayActor(c.actor)}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main Evidence page
// ============================================================================
export default function AdminEvidence() {
  const [dashboard, setDashboard] = useState(null);
  const [types, setTypes] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [frameworkFilter, setFrameworkFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [dash, ts, list] = await Promise.all([
        api.get("/admin/evidence/dashboard"),
        api.get("/admin/evidence/types"),
        api.get("/admin/evidence", { params: {
          q: q || undefined,
          status: statusFilter || undefined,
          framework: frameworkFilter || undefined,
          evidence_type: typeFilter || undefined,
        }}),
      ]);
      setDashboard(dash.data);
      setTypes(ts.data);
      setRows(list.data.evidence || []);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load evidence"));
    } finally { setLoading(false); }
  }, [q, statusFilter, frameworkFilter, typeFilter]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const coverage = dashboard?.evidence_coverage_pct ?? 0;
  const coverageTone = coverage >= 80 ? "pass" : coverage >= 50 ? "warn" : "fail";

  return (
    <div className="hw-uat-cycles" data-testid="admin-evidence">
      <div className="hw-uat-cycles__header">
        <div>
          <h2>
            <FolderKanban className="w-5 h-5" style={{ display: "inline", marginRight: "0.4rem", verticalAlign: "text-bottom" }} />
            Enterprise Evidence Management
          </h2>
          <p className="hw-uat-cycles__subtitle">
            Centralized repository for policies, procedures,
            audit logs, screenshots, and release artifacts. Supports internal
            storage, external references (SharePoint / OneDrive / Google Drive
            / S3 / Azure Blob / network shares), and hash-only integrity
            verification.
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button type="button" className="hw-reg__btn" onClick={loadAll}
                  data-testid="evidence-refresh">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button type="button" className="hw-reg__btn hw-reg__btn--primary"
                  onClick={() => setCreating(true)}
                  data-testid="evidence-add-btn">
            <Plus className="w-4 h-4" /> Add evidence
          </button>
        </div>
      </div>

      {dashboard ? (
        <section className="hw-ev__dashboard" data-testid="evidence-dashboard">
          <div className="hw-ev__grid">
            <KpiCard label="Total evidence" value={dashboard.total_evidence}
                     icon={Layers} testId="ev-kpi-total" />
            <KpiCard label="Evidence coverage"
                     value={`${coverage}%`}
                     tone={coverageTone} icon={ShieldCheck}
                     hint={`${dashboard.controls_with_evidence}/${dashboard.total_controls} controls covered`}
                     testId="ev-kpi-coverage" />
            <KpiCard label="Missing controls"
                     value={dashboard.missing_evidence_controls}
                     icon={AlertTriangle}
                     tone={dashboard.missing_evidence_controls === 0 ? "pass" : "warn"}
                     testId="ev-kpi-missing" />
            <KpiCard label="Expiring soon"
                     value={dashboard.expiring_evidence}
                     tone={dashboard.expiring_evidence === 0 ? "pass" : "warn"}
                     icon={Clock} testId="ev-kpi-expiring" />
            <KpiCard label="Expired"
                     value={dashboard.expired_evidence}
                     tone={dashboard.expired_evidence === 0 ? "pass" : "fail"}
                     icon={AlertTriangle} testId="ev-kpi-expired" />
          </div>
          <div className="hw-ev__breakdown">
            <div><strong>By framework:</strong> {
              Object.entries(dashboard.by_framework || {}).map(([k, v]) =>
                <span key={k} className="hw-ev__mini-chip">{k}: {v}</span>) || "—"
            }</div>
            <div><strong>By owner:</strong> {
              Object.entries(dashboard.by_owner || {}).slice(0, 6).map(([k, v]) =>
                <span key={k} className="hw-ev__mini-chip">{k}: {v}</span>) || "—"
            }</div>
          </div>
        </section>
      ) : null}

      <section className="hw-ev__list-sec">
        <h3>Evidence Repository <span className="hw-reg__count">({rows.length})</span></h3>
        <div className="hw-reg__filters">
          <div className="hw-reg__search">
            <Search className="w-4 h-4" />
            <input type="text" placeholder="Search title, tag, file name…"
                   value={q} onChange={(e) => setQ(e.target.value)}
                   data-testid="evidence-search" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  data-testid="evidence-status-filter">
            <option value="">All statuses</option>
            {Object.entries(STATUS_TONE).map(([k, v]) =>
              <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={frameworkFilter} onChange={(e) => setFrameworkFilter(e.target.value)}
                  data-testid="evidence-framework-filter">
            <option value="">All frameworks</option>
            <option value="hipaa">HIPAA</option>
            <option value="cfr42_part2">42 CFR Part 2</option>
            <option value="soc2">SOC 2</option>
            <option value="nist_csf">NIST CSF</option>
          </select>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                  data-testid="evidence-type-filter">
            <option value="">All types</option>
            {(types?.evidence_types || []).map(t =>
              <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="hw-uat-cycles__loading">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="hw-uat-cycles__empty" data-testid="evidence-empty">
            <FolderKanban className="w-6 h-6" />
            <div><strong>No evidence yet.</strong></div>
            <div style={{ fontSize: "0.85rem", color: "#64748B" }}>
              Click <em>Add evidence</em> to start populating the repository.
            </div>
          </div>
        ) : (
          <div className="hw-ev__rows" data-testid="evidence-list">
            {rows.map((r) => (
              <button key={r.evidence_id} type="button" className="hw-ev__row"
                      onClick={() => setSelected(r.evidence_id)}
                      data-testid={`evidence-row-${r.evidence_id}`}>
                <div className="hw-ev__row-main">
                  <div className="hw-ev__row-title">
                    <code>{r.evidence_id}</code>
                    <span>{r.title}</span>
                  </div>
                  <div className="hw-ev__row-meta">
                    <Pill tone={STATUS_TONE[r.status]}>{STATUS_TONE[r.status]?.label}</Pill>
                    <span className="hw-ev__chip">{r.evidence_type}</span>
                    <span className="hw-ev__chip">{STORAGE_LABEL[r.storage_model]}</span>
                    {(r.frameworks || []).map(fw => (
                      <span key={fw} className="hw-ev__chip hw-ev__chip--fw">{fw}</span>
                    ))}
                    {r.expiration_date ? (
                      <span className="hw-ev__chip">
                        <Clock className="w-3 h-3" /> {fmtDate(r.expiration_date)}
                      </span>
                    ) : null}
                    <span className="hw-ev__chip hw-ev__chip--hash"
                          title={r.integrity?.sha256 ? `Full SHA-256: ${r.integrity.sha256}` : "No hash recorded"}>
                      SHA-256: <code>{r.integrity?.sha256 ? `${String(r.integrity.sha256).slice(0, 12)}…` : "—"}</code>
                    </span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4" style={{ color: "#94A3B8" }} />
              </button>
            ))}
          </div>
        )}
      </section>

      {selected && (
        <EvidenceDetail evidenceId={selected}
                        onClose={() => setSelected(null)}
                        onChanged={loadAll} />
      )}
      {creating && types && (
        <CreateEvidenceDialog types={types}
                              onClose={() => setCreating(false)}
                              onCreated={() => { setCreating(false); loadAll(); }} />
      )}
    </div>
  );
}
